<script setup lang="ts">
import { computed, ref, toRef, watch } from "vue";
import { downloadPushToTalk, listPushToTalk, PttItem } from "../utils/s3";
import moment from "@/utils/moment";
import KboTable from "@/components/common/KboTable.vue";
import { useStore } from "@/utils/useStore";
import TaxiLicenseInput from "@/components/common/forms/TaxiLicenseInput.vue";
import OperatorSelect from "@/components/common/forms/OperatorSelect.vue";
import AudioPlayer from "@/components/common/AudioPlayer.vue";

const store = useStore();

const props = defineProps<{
  open: boolean;
}>();

const callcenter = store.getters["app/callCenter"];
const wgs = callcenter.wgs.map((wg: { id: number }) => wg.id);
const centrals = callcenter.wgs.flatMap((wg: { centrals: { id: number }[] }) =>
  wg.centrals.map((central: { id: number }) => central.id)
);

const wg = store.getters["app/defaultWorkgroup"];
const wgId = wg.id;

const open = toRef(props, "open");

const loading = ref(false);

watch(open, (value) => {
  if (value) {
    fetchData();
  }
});

const handleSearch = () => {
  filterData();
};

const emit = defineEmits<{
  (e: "update:open", value: boolean): void;
}>();

const tableData = ref<PttItem[]>([]);
const filteredData = ref<PttItem[]>([]);

const audioPlayerVisible = ref(false);
const currentAudio = ref<{
  url: string;
  title: string;
  origin: string;
  destination: string;
  date: Date;
}>({
  url: "",
  title: "",
  origin: "",
  destination: "",
  date: new Date(),
});

const handleClose = () => {
  emit("update:open", false);
};

const filter = ref<{
  from: Date;
  to?: Date;
  origin?: string;
  destination?: string;
  originId?: number;
  destinationId?: number;
}>({
  from: moment()().subtract(48, "hours").toDate(),
  to: undefined,
  origin: undefined,
  destination: undefined,
  originId: undefined,
  destinationId: undefined,
});

const filterData = () => {
  filteredData.value = tableData.value.filter((row) => {
    return (
      moment()(row.date).isAfter(filter.value.from) &&
      moment()(row.date).isBefore(filter.value.to) &&
      (!filter.value.origin || row.metadata.origin === filter.value.origin) &&
      (!filter.value.destination || row.metadata.destination === filter.value.destination) &&
      (!filter.value.originId || row.metadata.originid === filter.value.originId) &&
      (!filter.value.destinationId || row.metadata.destinationid === filter.value.destinationId)
    );
  });
};

const fetchData = async () => {
  loading.value = true;
  const data = await listPushToTalk(wgs, centrals, moment()().subtract(48, "hours").toDate());
  tableData.value = data;
  filterData();
  loading.value = false;
};

const columns = [
  {
    label: "Fecha",
    key: "date",
    width: "120px",
    renderer: (date: Date) => {
      return moment()(date).format("DD/MM/YYYY HH:mm");
    },
  },
  {
    label: "Central",
    key: "metadata.central",
    width: "140px",
  },
  {
    label: "Origen",
    key: "origin",
    width: "1fr",
  },
  {
    label: "Destino",
    key: "destination",
    width: "1fr",
  },
];

const handleRowClick = async (row: PttItem) => {
  const audio = await downloadPushToTalk(row.key);
  const audioUrl = URL.createObjectURL(audio);

  currentAudio.value = {
    url: audioUrl,
    title: row.title,
    origin: row.origin,
    destination: row.destination,
    date: row.date,
  };

  audioPlayerVisible.value = true;
};

const datePickerToOptions = computed(() => {
  return {
    disabledDate: (date: Date) => moment()(date).isBefore(moment()(filter.value.from)),
  };
});

const datePickerFromOptions = computed(() => {
  return {
    disabledDate: (date: Date) =>
      moment()(date).isBefore(moment()().subtract(48, "hours").toDate()),
  };
});
</script>

<template>
  <el-drawer :visible="open" title="Histórico" size="70%" @close="handleClose">
    <div class="drawer-content">
      <el-form label-position="top" size="mini" @submit.prevent.native="handleSearch">
        <el-row :gutter="20">
          <el-col :span="4">
            <el-form-item label="Desde">
              <el-date-picker
                v-model="filter.from"
                style="width: 100%"
                type="datetime"
                format="dd/MM/yyyy HH:mm"
                :picker-options="datePickerFromOptions"
              />
            </el-form-item>
          </el-col>
          <el-col :span="4">
            <el-form-item label="Hasta">
              <el-date-picker
                v-model="filter.to"
                style="width: 100%"
                type="datetime"
                format="dd/MM/yyyy HH:mm"
                :picker-options="datePickerToOptions"
              />
            </el-form-item>
          </el-col>
          <el-col :span="filter.origin === 'taxi' || filter.origin === 'agent' ? 3 : 6">
            <el-form-item label="Origen">
              <el-select v-model="filter.origin" clearable>
                <el-option label="Agente" value="agent" />
                <el-option label="Taxi" value="taxi" />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col v-if="filter.origin === 'taxi' || filter.origin === 'agent'" :span="3">
            <el-form-item :label="filter.origin === 'agent' ? 'Agente' : 'Taxi'">
              <template v-if="filter.origin === 'agent'">
                <operator-select v-model="filter.originId" />
              </template>
              <template v-if="filter.origin === 'taxi'">
                <taxi-license-input
                  v-model="filter.originId"
                  :workgroup-id="wgId"
                  class="taxi-license"
                  error-message="Licencia inválida"
                  placeholder="Nº licencia"
                />
              </template>
            </el-form-item>
          </el-col>
          <el-col :span="filter.destination === 'taxi' || filter.destination === 'agent' ? 3 : 6">
            <el-form-item label="Destino">
              <el-select v-model="filter.destination" clearable>
                <el-option label="Agente" value="agent" />
                <el-option label="Taxi" value="taxi" />
                <el-option label="Flota" value="fleet" />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col v-if="filter.destination === 'taxi'" :span="3">
            <el-form-item label="Taxi">
              <taxi-license-input
                v-model="filter.destinationId"
                :workgroup-id="wgId"
                class="taxi-license"
                error-message="Licencia inválida"
                placeholder="Nº licencia"
              />
            </el-form-item>
          </el-col>
          <el-col :span="2">
            <el-button size="mini" class="w-full mt-4" type="primary" native-type="submit">
              Buscar
            </el-button>
          </el-col>
          <el-col :span="2">
            <el-button size="mini" class="w-full mt-4" type="default" @click="fetchData">
              <i class="fas fa-sync-alt"></i>
            </el-button>
          </el-col>
        </el-row>
      </el-form>
      <KboTable
        :loading="loading"
        row-key-field="key"
        :items="filteredData"
        :columns="columns"
        @rowClicked="handleRowClick"
      />
      <AudioPlayer
        :visible="audioPlayerVisible"
        :audio-url="currentAudio.url"
        :title="currentAudio.title"
        :origin="currentAudio.origin"
        :destination="currentAudio.destination"
        :date="currentAudio.date"
        @update:visible="audioPlayerVisible = $event"
      />
    </div>
  </el-drawer>
</template>
